import { z } from 'zod/v4';
import { CompanyDocumentSchema } from './company-document.js';
import {
  CompanyDocumentPermissionSchema,
  CompanyDocumentPrivacyStatusSchema,
} from './get-by-id.js';

/**
 * Sort keys the list accepts. The legacy schema also advertised `createdBy`, but
 * its ORDER BY joined a `users` table this query never had — every request that
 * used it failed with a 500 — so it is not carried over.
 */
export const COMPANY_DOCUMENT_LIST_SORT_COLUMNS = [
  'name',
  'type',
  'updatedAt',
  'companyName',
  'facilityProcesses',
  'id',
  'internalCompanyCode',
  'notes',
] as const;

export type CompanyDocumentListSortColumn =
  (typeof COMPANY_DOCUMENT_LIST_SORT_COLUMNS)[number];

const sortColumns = COMPANY_DOCUMENT_LIST_SORT_COLUMNS.join('|');
// `col:DIR`, comma-separated for a multi-column sort. Direction is upper-case
// only, as the legacy pattern was.
const SORT_PATTERN = new RegExp(
  `^(${sortColumns}):(ASC|DESC)(,(${sortColumns}):(ASC|DESC))*$`,
);

/**
 * `undefined` must survive: `isNetworkShared=false` selects a different query
 * from leaving it out, so a missing flag cannot be folded into `false`.
 */
const optionalFlag = z
  .union([z.boolean(), z.enum(['true', 'false'])])
  .optional()
  .transform(v => (v === undefined ? undefined : v === true || v === 'true'));

export const ListCompanyDocumentsQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce.number().int().min(0).max(500).default(15),
  sort: z.string().regex(SORT_PATTERN).optional(),
  /** Case-insensitive substring of the document name. */
  q: z.string().max(255).optional(),
  /** JSON object of column filters. Malformed JSON is ignored, not rejected. */
  filter: z.string().max(512).optional(),
  /** Whose documents the plain (no-flag) list reads; defaults to the caller's company. */
  companyId: z.string().optional(),
  /** Hide the rows the caller may see only as an access-request placeholder. */
  onlyWithGrantedPermission: optionalFlag,
  /** Own documents plus everything the network has made visible to the caller. */
  isWithNetworkDocuments: optionalFlag,
  /** `false`: network documents the caller has no access to yet. Ignored when `isWithNetworkDocuments` is set. */
  isNetworkShared: optionalFlag,
});

export type ListCompanyDocumentsQuery = z.infer<
  typeof ListCompanyDocumentsQuerySchema
>;

export const CompanyDocumentListPrivacyStatusSchema =
  CompanyDocumentPrivacyStatusSchema.extend({
    /** On `shared_to` / `shared_by`: the other party. */
    company: z
      .object({ id: z.string(), name: z.string().nullable() })
      .optional(),
  });

export type CompanyDocumentListPrivacyStatus = z.infer<
  typeof CompanyDocumentListPrivacyStatusSchema
>;

export const CompanyDocumentListItemSchema = CompanyDocumentSchema.extend({
  companyName: z.string().nullable(),
  requestCompanyFacilityProcesses: z.string().nullable(),
  /** The caller's own notes about the document's company. */
  internalCompanyCode: z.string().nullable(),
  notes: z.string().nullable(),
  updatedByUserName: z.string().nullable(),
  updatedByCompanyName: z.string().nullable(),
  /** Absent on a row the caller cannot open — see `permission`. */
  privacyStatus: CompanyDocumentListPrivacyStatusSchema.optional(),
  /**
   * Present **only** on a placeholder row: the caller cannot open the document,
   * so `name` and every file field are blanked. Its absence is the access grant.
   */
  permission: z
    .object({
      status: z.enum(['requested', 'none']),
      data: CompanyDocumentPermissionSchema.nullable(),
    })
    .optional(),
});

export type CompanyDocumentListItem = z.infer<
  typeof CompanyDocumentListItemSchema
>;
